
import { Content, Language } from './types';

export const CONTENT: Record<Language, Content> = {
  en: {
    nav: {
      vision: "Vision",
      studio: "Studio",
      academy: "Academy",
      community: "Community",
      contact: "Contact",
      geoai: "GeoAI Agent",
      download: "Download",
      pricing: "Pricing"
    },
    hero: {
      headline: "Automate Spatial Decisions. Reduce Operational Risk.",
      subline: "We build Python & GeoAI pipelines that cut logistics costs and ensure EUDR compliance.",
      ctaPrimary: "Book a Spatial Audit", 
      ctaSecondary: "View Case Studies"    
    },
    about: {
      label: "WHO WE ARE",
      title: "The Architects of Spatial Intelligence",
      description: "VEXL is a specialized institution bridging the gap between academic theory and market reality. We provide elite engineering support, turning complex geospatial data into masterpiece visualizations and actionable insights.",
      stats: [
        { value: "500+", label: "Engineers Mentored" },
        { value: "3", label: "Partner Universities" },
        { value: "100%", label: "Thesis Success" }
      ]
    },
    philosophy: {
        title: "Beyond Coordinates.",
        intro: "For decades, maps answered the question: 'Where is it?' At VEXL, we answer the question that matters most: 'What is it worth?' We believe that geography is not just a backdrop for human activity; it is the invisible engine of the economy.",
        pillars: [
            {
                title: "The Spatial Truth",
                desc: "Data doesn't lie. Satellite imagery reveals the pulse of a city—wealth, movement, and decay—better than any financial report. We trust the pixel over the opinion."
            },
            {
                title: "Economic Gravity",
                desc: "Just as planets have gravity, locations have economic pull. Our mission is to measure this force, predicting where businesses will thrive and where they will fade."
            },
            {
                title: "The Time Dimension",
                desc: "A map is a snapshot of the past. VEXL adds the dimension of 'Time'. We don't just show you the map of today; using AI, we show you the map of tomorrow."
            }
        ]
    },
    impact: {
        title: "Measurable Change.",
        intro: "Impact is not a buzzword; it's our metric for success. VEXL operates on two fronts: Empowering the next generation of Geo-Scientists and securing the future of Enterprise Investments.",
        academic: {
            title: "Academic Impact",
            desc: "Bridging the Gap. Universities teach theory. VEXL teaches reality. Through our Academy and Studio, we have empowered students to master modern GIS tools, transforming graduation projects into market-ready solutions.",
            stat: "+50 Engineered Projects"
        },
        commercial: {
            title: "Commercial Impact",
            desc: "Risk Elimination. Every wrong location decision costs millions. By digitizing site selection, VEXL aims to reduce commercial real estate failure rates in Egypt by 20% by 2030.",
            stat: "Precision in Every Pixel"
        },
        caseStudy: {
            title: "Project: Nour Pharmacies Optimization",
            desc: "Using spatial accessibility analysis, we helped identifying coverage gaps, proving that data-driven expansion creates direct ROI."
        }
    },
    services: {
      sectionTitle: "VEXL STUDIO SERVICES",
      academic: {
        title: "ACADEMIC CONSULTANCY",
        status: "OPEN FOR COMMISSION",
        desc: "Premium engineering support for researchers and final-year students. We don't just execute; we mentor you to build industry-standard projects.",
        items: [
          "Advanced Cartography & Visualization",
          "Spatial Analysis Consultation",
          "Graduation Project Mentorship Program",
          "Thesis Data Modeling"
        ],
        cta: "Request Consultation"
      },
      corporate: {
        title: "ENTERPRISE DIVISION",
        status: "RESTRICTED",
        desc: "B2B Spatial Intelligence. Currently dedicated to long-term strategic partners only."
      }
    },
    b2bPackages: [
      {
        title: "SpatialAudit™",
        subtitle: "Geospatial Data Infrastructure Assessment",
        highlight: "Identify bottlenecks, route optimization gaps.",
        delivery: "2-week delivery"
      },
      {
        title: "GeoFlow™ Pipeline",
        subtitle: "Python Geospatial ETL & Analytics Automation",
        highlight: "Custom GeoPandas pipeline, automated dashboards.",
        delivery: "4-6 weeks delivery"
      },
      {
        title: "GeoAI Insight Engine™",
        subtitle: "AI-Powered Satellite & Remote Sensing Analytics",
        highlight: "EUDR compliance, change detection.",
        delivery: "Determined per scope"
      }
    ],
    b2bCaseStudies: [
      {
        industry: "Logistics Optimization",
        description: "Restructured spatial algorithms resulting in a 40% increase in location accessibility for a major pharmacy chain."
      },
      {
        industry: "Market Intelligence",
        description: "Engineered an automated ETL pipeline analyzing 85,000+ spatial data points, uncovering critical market risk zones in NYC."
      }
    ],
    studio: {
        academicFaq: {
            title: "Academic Protocols",
            items: [
                {
                    q: "Do you sell ready-made projects?",
                    a: "No. We are an engineering consultancy, not a project shop. We execute the complex technical layers (Analysis, Modeling) and mentor you to own and defend your work."
                },
                {
                    q: "I am worried about the defense panel.",
                    a: "Fear not. Our package includes a 'Simulated Defense' session and a technical dossier with expected questions and model answers."
                },
                {
                    q: "What software do you use?",
                    a: "Industry standards only: ArcGIS Pro, QGIS, Python (GeoPandas), and Web GIS frameworks for interactive dashboards."
                },
                {
                    q: "How is the cost determined?",
                    a: "Based on scope (Data size, Analysis complexity, Output type). Submit a 'Consultation Request' for a precise technical and financial proposal."
                }
            ]
        },
        corporateFaq: {
            title: "Enterprise Protocols",
            items: [
                {
                    q: "How do you handle data privacy?",
                    a: "Strict NDA protocols. Data is processed on secure, isolated environments and purged 14 days after delivery. We respect full data sovereignty."
                },
                {
                    q: "Do you offer real-time monitoring?",
                    a: "Yes. We engineer custom Web GIS Dashboards that connect to your live databases for real-time fleet or asset tracking."
                },
                {
                    q: "What industries do you serve?",
                    a: "Retail Expansion, Logistics & Supply Chain, Real Estate Valuation, and Urban Planning."
                }
            ]
        }
    },
    alliances: {
      title: "STRATEGIC ALLIANCES",
      logos: [
        { name: "Cairo University", type: 'university' },
        { name: "Ain Shams University", type: 'university' },
        { name: "Alexandria University", type: 'university' },
        { name: "King Salman International University", type: 'university', image: '/ksiu-logo-placeholder.png' },
        { name: "Nour Pharmacies", type: 'corporate' }
      ]
    },
    testimonials: {
      sectionTitle: "IMPACT REPORT",
      title: "Voices from the Grid",
      subtitle: "Feedback from our ecosystem.",
      items: [
        {
          name: "Omar Khaled",
          role: "GIS Student @ Cairo Uni",
          rating: 5,
          text: "The Mentorship Program saved my graduation project. They didn't just give me the maps; they taught me how to defend my analysis in front of the panel.",
          lang: 'ar'
        },
        {
          name: "Dr. Ahmed Nour",
          role: "CEO, Nour Chains",
          rating: 4.5,
          text: "Professional execution. The smart locator tool solved a major logistics issue for our branches. The UI is sleek, though we hope for faster updates next time. Highly recommended.",
          lang: 'en'
        },
        {
          name: "Sarah Magdy",
          role: "MSc Researcher",
          rating: 5,
          text: "I struggled with the Spatial Analysis model for months. VEXL Studio solved the logic in 3 days. Worth every penny for the peace of mind.",
          lang: 'mix'
        },
        {
          name: "M. Hassan",
          role: "Senior Backend Dev",
          rating: 4,
          text: "Impressive data visualization techniques. They treat maps like art. Looking forward to seeing the R&D division go live.",
          lang: 'en'
        }
      ]
    },
    caseStudy: {
      label: "CASE STUDY // NOUR PHARMACIES",
      title: "Smart Branch Locator",
      desc: "Customer routing tool powered by GPS & spatial logic. Mobile-first execution.",
      metric: "Optimized Delivery Routes & ROI",
      cta: "Read Full Case Study",
      image: "/my-map.webp",
      
      details: {
        problem: {
          title: "The Problem",
          text: "Customers were struggling to find the nearest pharmacy branch that had specific medication in stock, leading to lost sales and customer frustration."
        },
        solution: {
          title: "The VEXL Solution",
          text: "We engineered a custom Geolocation API integrated with their inventory system. A user-friendly map interface now guides customers to the exact location with real-time stock availability."
        },
        result: {
          title: "The Result",
          text: "40% increase in foot traffic to remote branches and a significant reduction in call center inquiries regarding location."
        }
      }
    },
    academy: {
      title: "VEXL Academy",
      subtitle: "Building the next generation of GeoAI Engineers.",
      cta: "Apply to Join",
      tagline: "Invite Only. Elite Squad.",
      faq: {
          title: "Academy FAQ",
          items: [
              {
                  q: "Do I need prior coding experience?",
                  a: "Not for the Foundation Track. We start from zero. For the Advanced GeoAI Track, basic Python knowledge is recommended."
              },
              {
                  q: "Is this a recorded course?",
                  a: "No. VEXL Academy relies on 'Live Mentorship'. You work on real-world projects under the supervision of senior engineers, not just watching tutorials."
              },
              {
                  q: "What certificates do I get?",
                  a: "You receive a 'Project Portfolio' validation. In the tech industry, a working GitHub repository is worth 10x more than a paper certificate."
              },
              {
                  q: "How do I join?",
                  a: "Admissions are selective. Submit your dossier via the 'Join Protocol'. We review applicants based on passion and logical thinking, not just grades."
              }
          ]
      }
    },
    vetting: {
        title: "COHORT 04 // VETTING PROTOCOL",
        subtitle: "Complete the dossier below. Only elite candidates will receive a callback.",
        fields: {
            name: "Full Name",
            university: "University / Year",
            project: "Best Project Link (Drive/GitHub)",
            why: "Why VEXL? (Your Mission)",
            skill: "GIS & Python Proficiency"
        },
        skillOptions: ["Novice", "Intermediate", "Elite"],
        submit: "Submit Dossier",
        successTitle: "Application Received",
        successDesc: "Status: Under Review. We will analyze your qualifications and contact you if you meet the protocol."
    },
    rnd: {
      title: "VEXL LABS // R&D",
      desc: "Next-Gen Retail Intelligence Systems.",
      status: "STATUS: TOP SECRET",
      redacted: "We are cooking something revolutionary for the Retail Industry. Stay Tuned."
    },
    legal: {
        privacy: {
            title: "Privacy & Data Sovereignty Policy",
            lastUpdated: "Effective Date: Launch 2026 // Version 2.4",
            sections: [
                {
                    heading: "1. Sovereign Commitment & Scope",
                    content: [
                        "VEXL Ecosystem operates as an enterprise Spatial AI Engine and sovereign geodetic intelligence layer. We are committed to uncompromised data privacy and data sovereignty.",
                        "This policy governs all data ingestion, processing, and storage across VEXL Nova, GeoAgent, CropLens, DroughtWatch, and desktop API interfaces, complying with the Egyptian Personal Data Protection Law No. 151 of 2020 and international data privacy frameworks."
                    ]
                },
                {
                    heading: "2. Spatial Data Architecture & Ingestion",
                    content: [
                        "We process identity data (name, enterprise email, billing credentials managed via secure third-party gateways), technical telemetry (browser, operating system, IP coordinates), and spatial assets.",
                        "Spatial assets include vector boundaries (Shapefiles, GeoJSON, KML, Geopackages), raster tensors (satellite multi-spectral bands), and computational spatial queries.",
                        "All spatial datasets are ingested into secure, isolated memory enclaves solely for the execution of your requested spatial analyses."
                    ]
                },
                {
                    heading: "3. Zero-Egress & Non-Training Guarantee",
                    content: [
                        "Zero Customer Data Training: VEXL strictly and irrevocably guarantees that your proprietary spatial geometries, polygon boundaries, sensor imagery, and confidential prompt sessions will NEVER be used to train, retrain, fine-tune, or benchmark public or third-party AI models.",
                        "Ephemeral Memory Sandboxing: Uploaded shapefiles and raster scenes are processed in memory and never retained on public persistent storage without explicit user workspace archival commands.",
                        "Air-Gapped Sovereign Readiness: For defense and enterprise clients, VEXL supports air-gapped on-premise deployments where 0% of telemetry or data ever leaves the sovereign perimeter."
                    ]
                },
                {
                    heading: "4. Infrastructure Security & Cryptography",
                    content: [
                        "All data in transit is encrypted using TLS 1.3 with forward secrecy. Data at rest is encrypted utilizing AES-256 cryptographic standards.",
                        "Underlying hosting and database infrastructure maintains SOC 2 Type II and ISO 27001 compliance standards. Access controls enforce strict least-privilege Role-Based Access Control (RBAC)."
                    ]
                },
                {
                    heading: "5. Data Retention, Portability & Erasure",
                    content: [
                        "Users retain absolute sovereignty over their spatial workspaces, generated ArcPy scripts, and chat records.",
                        "You may export your complete analytical history or execute a permanent, cryptographic purge of all account records and spatial caches at any time via account settings or upon request."
                    ]
                },
                {
                    heading: "6. Data Protection Officer & Inquiries",
                    content: [
                        "For data privacy inquiries, enterprise DPA agreements, or compliance verification requests, contact our designated Data Protection Office at: vexl.gis@gmail.com"
                    ]
                }
            ]
        },
        terms: {
            title: "Terms of Service & Spatial Compute Agreement",
            lastUpdated: "Effective Date: Launch 2026 // Version 2.4",
            sections: [
                {
                    heading: "1. Agreement & Platform Scope",
                    content: [
                        "By accessing or utilizing the VEXL Ecosystem, including the VEXL Nova OS, GeoAgent, CropLens, DroughtWatch, ArcPy scripting engines, desktop tools, and spatial APIs, you agree to be bound by these Terms of Service.",
                        "These terms constitute a legally binding agreement between you (or your institutional organization) and VEXL."
                    ]
                },
                {
                    heading: "2. Intellectual Property & Official Software Copyright",
                    content: [
                        "VEXL Sovereign Assets: The VEXL Ecosystem, including the core Spatial AI runtime, tensor-topology algorithms, UI design systems, documentation, and the VEXL mark, are protected under international copyright treaties and officially registered with the Egyptian Intellectual Property Authority (EGIPA Deposit No. 5007 under Law No. 82 of 2002 and Law No. 163 of 2023).",
                        "User Outputs & Scripts: You retain full ownership of all custom ArcPy scripts, geospatial models, reports, and vector outputs generated through your interaction with the platform."
                    ]
                },
                {
                    heading: "3. Spatial Compute Quotas & Commercial Subscriptions",
                    content: [
                        "Service access is provided across designated tiers (Spatial Starter, Research Scholar, Spatial Engineer, and Sovereign Enterprise) governed by Spatial Compute Units and API rate limits.",
                        "Subscriptions are billed according to published schedules. We support institutional purchase orders, enterprise corporate agreements (SLA), and secure electronic payments.",
                        "Subscription cancellation may be executed at any time, retaining service access until the conclusion of the active billing cycle."
                    ]
                },
                {
                    heading: "4. Deterministic Accuracy & Engineering Responsibility",
                    content: [
                        "VEXL enforces rigorous Karney ellipsoidal geodesics, strict EPSG datum transformations, and physically calibrated satellite spectral math (NDVI, NDWI, EVI).",
                        "However, geospatial outputs are provided for technical analysis, scientific modeling, and decision support. Licensed engineers and spatial analysts remain responsible for validating mission-critical ground-truth data before executing civil or environmental implementations."
                    ]
                },
                {
                    heading: "5. Prohibited Activities & Platform Integrity",
                    content: [
                        "Users agree not to: (a) attempt to decompile, reverse-engineer, or extract weights of VEXL AI models; (b) execute automated scraping or denial-of-service attempts against VEXL infrastructure; (c) utilize the service for unauthorized weapons proliferation or malicious surveillance.",
                        "Violation of platform integrity will result in instantaneous API key revocation and account termination."
                    ]
                },
                {
                    heading: "6. Governing Law & Sovereign Jurisdiction",
                    content: [
                        "These terms and any disputes arising from or relating to the service are governed by and construed in accordance with the laws of the Arab Republic of Egypt.",
                        "The parties submit to the exclusive jurisdiction of the economic and commercial courts in Cairo, Egypt."
                    ]
                },
                {
                    heading: "7. Modifications & Contact Channel",
                    content: [
                        "VEXL reserves the right to modify these terms to reflect regulatory, security, or platform enhancements with 30-day advance notice for enterprise accounts.",
                        "For institutional inquiries, legal notices, or enterprise SLA contracting, contact: vexl.gis@gmail.com"
                    ]
                }
            ]
        }
    },
    footer: {
      phrase: "Prediction in Every Pixel.",
      copyright: "VEXL © 2026.",
      links: {
        download: "Download App",
        pricing: "Pricing",
        help: "Help Center"
      }
    },
  },
  ar: {
    nav: {
      vision: "الرؤية",
      studio: "الاستوديو",
      academy: "الأكاديمية",
      community: "المجتمع",
      contact: "تواصل",
      geoai: "وكيل GeoAI",
      download: "تحميل",
      pricing: "الأسعار"
    },
    hero: {
      headline: "أتمتة القرارات المكانية. تقليل المخاطر التشغيلية.",
      subline: "نبني خطوط أنابيب بايثون و GeoAI لخفض تكاليف اللوجستيات وضمان التوافق مع EUDR.",
      ctaPrimary: "تدقيق مكاني",
      ctaSecondary: "دراسات الحالة"
    },
    about: {
      label: "من نحن",
      title: "مهندسو الذكاء المكاني",
      description: "نحن في VEXL لسنا مجرد مؤسسة؛ نحن تحالف من المهندسين وعلماء البيانات. نقدم دعماً هندسياً للنخبة، ونحول البيانات الجغرافية المعقدة إلى لوحات فنية وحلول واقعية.",
      stats: [
        { value: "+500", label: "مهندس تم توجيهه" },
        { value: "3", label: "جامعات شريكة" },
        { value: "100%", label: "نسبة نجاح المشاريع" }
      ]
    },
    philosophy: {
        title: "ما وراء الإحداثيات.",
        intro: "لعقود من الزمن، أجابت الخرائط على سؤال: 'أين؟'. في VEXL، نجيب على السؤال الأهم: 'ما هي القيمة؟'. نحن نؤمن أن الجغرافيا ليست مجرد خلفية للنشاط البشري؛ بل هي المحرك الخفي للاقتصاد.",
        pillars: [
            {
                title: "الحقيقة المكانية",
                desc: "البيانات لا تكذب. صور الأقمار الصناعية تكشف نبض المدينة—الثراء، الحركة، والاضمحلال—أفضل من أي تقرير مالي. نحن نثق في البكسل أكثر من الرأي."
            },
            {
                title: "الجاذبية الاقتصادية",
                desc: "كما للكواكب جاذبية، للمواقع جاذبية اقتصادية. مهمتنا هي قياس هذه القوة، والتنبؤ أين ستزدهر الشركات وأين ستتلاشى."
            },
            {
                title: "البعد الزمني",
                desc: "الخريطة هي لقطة للماضي. VEXL تضيف بعد 'الزمن'. نحن لا نعرض لك خريطة اليوم فقط؛ باستخدام الذكاء الاصطناعي، نعرض لك خريطة الغد."
            }
        ]
    },
    impact: {
        title: "تغيير ملموس.",
        intro: "الأثر ليس مجرد كلمة رنانة؛ إنه مقياس نجاحنا. تعمل VEXL على جبهتين: تمكين الجيل القادم من علماء البيانات الجغرافية وتأمين مستقبل الاستثمارات المؤسسية.",
        academic: {
            title: "الأثر الأكاديمي",
            desc: "سد الفجوة. الجامعات تدرس النظريات، وVEXL تدرس الواقع. من خلال أكاديميتنا والاستوديو، قمنا بتمكين الطلاب من إتقان أدوات نظم المعلومات الجغرافية الحديثة، وتحويل مشاريع التخرج إلى حلول جاهزة للسوق.",
            stat: "+50 مشروع هندسي"
        },
        commercial: {
            title: "الأثر التجاري",
            desc: "القضاء على المخاطر. كل قرار موقع خاطئ يكلف الملايين. من خلال رقمنة اختيار المواقع، تهدف VEXL إلى تقليل معدلات فشل العقارات التجارية في مصر بنسبة 20% بحلول عام 2030.",
            stat: "الدقة في كل بكسل"
        },
        caseStudy: {
            title: "مشروع: تحسين صيدليات نور",
            desc: "باستخدام تحليل الوصول المكاني، ساعدنا في تحديد فجوات التغطية، مما أثبت أن التوسع القائم على البيانات يخلق عائداً مباشراً على الاستثمار."
        }
    },
    services: {
      sectionTitle: "خدمات استوديو VEXL",
      academic: {
        title: "الاستشارات الأكاديمية",
        status: "متاح للتعاقد",
        desc: "دعم هندسي احترافي للباحثين وطلاب السنوات النهائية. نحن لا ننفذ المشروع فحسب، بل نقدم برنامج إرشاد متكامل لتتقن مشروعك.",
        items: [
          "تصميم الخرائط المتقدم (Advanced Cartography)",
          "استشارات التحليل المكاني (Spatial Analysis)",
          "برنامج إرشاد مشاريع التخرج (Mentorship)",
          "نمذجة البيانات للرسائل العلمية"
        ],
        cta: "اطلب استشارة"
      },
      corporate: {
        title: "قطاع الأعمال",
        status: "نطاق مقيد",
        desc: "حلول الذكاء المكاني للشركات. متاح حالياً للشركاء الاستراتيجيين فقط."
      }
    },
    b2bPackages: [
      {
        title: "التقييم المكاني (SpatialAudit™)",
        subtitle: "تقييم البنية التحتية للبيانات المكانية",
        highlight: "تحديد الاختناقات وثغرات تحسين المسارات.",
        delivery: "تسليم في أسبوعين"
      },
      {
        title: "أنظمة تدفق البيانات (GeoFlow™)",
        subtitle: "أتمتة الـ ETL والتحليل المكاني",
        highlight: "خطوط أنابيب GeoPandas مخصصة ولوحات معلومات.",
        delivery: "4-6 أسابيع"
      },
      {
        title: "محرك روئ الذكاء المكاني (GeoAI Insight™)",
        subtitle: "تحليلات الأقمار الصناعية بالذكاء الاصطناعي",
        highlight: "كشف التغيرات والتوافق مع EUDR.",
        delivery: "حسب النطاق"
      }
    ],
    b2bCaseStudies: [
      {
        industry: "تحسين اللوجستيات",
        description: "أعدنا هيكلة الخوارزميات المكانية مما أدى إلى زيادة بنسبة 40% في الوصول المكاني لشركة صيدليات كبرى."
      },
      {
        industry: "استخبارات السوق",
        description: "هندسة خط أنابيب ETL مؤتمت لتحليل أكثر من 85,000 نقطة بيانات جغرافية، لكشف مناطق المخاطر في مدينة نيويورك."
      }
    ],
    studio: {
        academicFaq: {
            title: "أسئلة الطلاب الشائعة",
            items: [
                {
                    q: "هل بتبيعوا مشاريع جاهزة؟",
                    a: "لا. نحن مكتب استشاري هندسي ولسنا 'دكان مشاريع'. نقوم بتنفيذ الجوانب التقنية المعقدة (التحليل، النمذجة) وندربك لتكون أنت صاحب المشروع وتناقشه بطلاقة."
                },
                {
                    q: "أنا قلقان جداً من مناقشة المشروع.",
                    a: "لا تقلق. خدمتنا تشمل جلسة 'محاكاة للمناقشة' (Simulated Defense) وملف فني يحتوي على الأسئلة المتوقعة من الدكاترة وإجاباتها النموذجية."
                },
                {
                    q: "إيه البرامج اللي بتشتغلوا بيها؟",
                    a: "نستخدم معايير السوق العالمية: ArcGIS Pro, QGIS, Python (GeoPandas)، وأطر عمل Web GIS للوحات المعلومات التفاعلية."
                },
                {
                    q: "إزاي بتحددوا التكلفة؟",
                    a: "بناءً على النطاق (حجم البيانات، تعقيد التحليل، نوع المخرجات). قدم 'طلب استشارة' وسنرسل لك عرضاً فنياً ومالياً دقيقاً."
                }
            ]
        },
        corporateFaq: {
            title: "بروتوكولات الشركات",
            items: [
                {
                    q: "كيف تضمنون سرية بياناتنا؟",
                    a: "نلتزم ببروتوكولات عدم الإفصاح (NDA) الصارمة. تتم معالجة البيانات في بيئات معزولة وآمنة، ويتم مسحها نهائياً بعد التسليم بـ 14 يوماً."
                },
                {
                    q: "هل تقدمون حلول متابعة لحظية؟",
                    a: "نعم. نقوم ببرمجة لوحات تحكم Web GIS مخصصة تتصل بقواعد بياناتك لعرض وتتبع الأصول أو الأسطول لحظياً."
                },
                {
                    q: "ما هي القطاعات التي تخدمونها؟",
                    a: "التوسع في قطاع التجزئة (Retail)، الخدمات اللوجستية، التقييم العقاري، والتخطيط العمراني."
                }
            ]
        }
    },
    alliances: {
      title: "التحالفات الاستراتيجية",
      logos: [
        { name: "جامعة القاهرة", type: 'university' },
        { name: "جامعة عين شمس", type: 'university' },
        { name: "جامعة الإسكندرية", type: 'university' },
        { name: "جامعة الملك سلمان الدولية", type: 'university', image: '/ksiu-logo-placeholder.png' },
        { name: "صيدليات نور", type: 'corporate' }
      ]
    },
    testimonials: {
      sectionTitle: "تقرير الأثر",
      title: "أصوات من الشبكة",
      subtitle: "آراء وتجارب منظومتنا.",
      items: [
        {
          name: "عمر خالد",
          role: "GIS Student @ Cairo Uni",
          rating: 5,
          text: "برنامج الإرشاد أنقذ مشروع تخرجي. المهندسين مش بس سلموني الخرائط، دول دربوني إزاي أناقش وأدافع عن تحليلاتي قدام اللجنة.",
          lang: 'ar'
        },
        {
          name: "د. أحمد نور",
          role: "CEO, Nour Chains",
          rating: 4.5,
          text: "Professional execution. The smart locator tool solved a major logistics issue for our branches. The UI is sleek, though we hope for faster updates next time. Highly recommended.",
          lang: 'en'
        },
        {
          name: "سارة مجدي",
          role: "باحثة ماجستير",
          rating: 5,
          text: "كنت تايهة في موديل التحليل المكاني بقالي شهور. استوديو VEXL حل اللوجيك في 3 أيام. بجد يستاهلوا كل جنيه.",
          lang: 'mix'
        },
        {
          name: "م. حسن",
          role: "Senior Backend Dev",
          rating: 4,
          text: "Impressive data visualization techniques. They treat maps like art. Looking forward to seeing the R&D division go live.",
          lang: 'en'
        }
      ]
    },
    caseStudy: {
      label: "دراسة حالة // صيدليات نور",
      title: "محدد الفروع الذكي",
      desc: "أداة توجيه عملاء مدعومة بالمنطق المكاني. تنفيذ متقن.",
      metric: "تحسين مسارات التوصيل و ROI",
      cta: "اقرأ التقرير الكامل",
      image: "/my-map.webp",

      details: {
        problem: {
          title: "المشكلة",
          text: "كان العملاء يواجهون صعوبة في العثور على أقرب فرع يتوفر فيه دواء معين، مما أدى إلى ضياع المبيعات وإحباط العملاء."
        },
        solution: {
          title: "حلول VEXL",
          text: "قمنا ببرمجة واجهة Geolocation API مخصصة وربطها بنظام المخزون. واجهة خرائط سهلة الاستخدام توجه العملاء الآن للموقع الدقيق مع توفر المخزون لحظياً."
        },
        result: {
          title: "النتيجة",
          text: "زيادة بنسبة 40% في زيارات الفروع البعيدة وانخفاض كبير في استفسارات مركز الاتصال بخصوص الموقع."
        }
      }
    },
    academy: {
      title: "أكاديمية VEXL",
      subtitle: "بناء الجيل القادم من مهندسي GeoAI.",
      cta: "تقدم للانضمام",
      tagline: "بدعوة فقط. نخبة.",
      faq: {
          title: "أسئلة شائعة",
          items: [
              {
                  q: "هل محتاج خبرة برمجة سابقة؟",
                  a: "ليس لمسار التأسيس. نبدأ معك من الصفر. أما مسار الـ GeoAI المتقدم، فيفضل وجود معرفة بسيطة بلغة بايثون."
              },
              {
                  q: "هل الكورس مسجل فيديوهات فقط؟",
                  a: "لا. أكاديمية VEXL تعتمد على 'الإرشاد المباشر'. أنت تعمل على مشاريع حقيقية من السوق تحت إشراف مهندسين كبار، ولا تكتفي بمشاهدة الفيديوهات."
              },
              {
                  q: "إيه الشهادات اللي بحصل عليها؟",
                  a: "تحصل على اعتماد لـ 'معرض أعمالك' (Portfolio). في سوق التكنولوجيا، الكود والمشاريع المرفوعة على GitHub لها قيمة تفوق الشهادات الورقية بـ 10 أضعاف."
              },
              {
                  q: "إزاي انضم للأكاديمية؟",
                  a: "القبول يتم بانتقائية. قدم ملفك عبر زر 'Join Protocol'. نراجع المتقدمين بناءً على الشغف والتفكير المنطقي، وليس الدرجات فقط."
              }
          ]
      }
    },
    vetting: {
        title: "الدفعة 04 // بروتوكول الفحص",
        subtitle: "أكمل الملف أدناه. سيتم التواصل مع المرشحين النخبة فقط.",
        fields: {
            name: "الاسم الكامل",
            university: "الجامعة / السنة",
            project: "رابط أفضل مشروع (Drive/GitHub)",
            why: "لماذا VEXL؟ (مهمتك)",
            skill: "مستوى الـ GIS & Python"
        },
        skillOptions: ["مبتدئ", "متوسط", "نخبة"],
        submit: "إرسال الملف",
        successTitle: "تم استلام الطلب",
        successDesc: "الحالة: قيد المراجعة. سنقوم بتحليل مؤهلاتك والتواصل معك إذا كنت تستوفي البروتوكول."
    },
    rnd: {
      title: "معامل VEXL // R&D",
      desc: "أنظمة ذكاء التجزئة للجيل القادم.",
      status: "الحالة: سري للغاية",
      redacted: "نحن نطبخ شيئاً ثورياً لقطاع التجزئة. ترقبوا."
    },
    legal: {
        privacy: {
            title: "وثيقة الخصوصية وسيادة البيانات المكانية",
            lastUpdated: "تاريخ السريان: إطلاق 2026 // الإصدار 2.4",
            sections: [
                {
                    heading: "١. الالتزام بالسيادة ونطاق السريان",
                    content: [
                        "تعمل منظومة VEXL كمحرك ذكاء اصطناعي مكاني وطبقة استخبارات جيوديسية سيادية. نحن ملتزمون بأعلى معايير الخصوصية وسيادة البيانات المكانية.",
                        "تحكم هذه الوثيقة عمليات استيعاب ومعالجة وحفظ البيانات عبر محرك VEXL Nova، وGeoAgent، وCropLens، وDroughtWatch، وواجهات برمجة التطبيقات لسطح المكتب، بما يتوافق مع أحكام قانون حماية البيانات الشخصية المصري رقم (151) لسنة 2020 والأطر الدولية المعتمدة."
                    ]
                },
                {
                    heading: "٢. معمارية البيانات والاستيعاب المكاني",
                    content: [
                        "نقوم بمعالجة بيانات الهوية والاعتماد المؤسسي، والبيانات التقنية الصادرة عن النظام والمتصفح، والأصول المكانية المقدمة من المستخدم.",
                        "تشمل الأصول المكانية الطبقات المتجهية (Vector) مثل Shapefiles وGeoJSON وKML وGeopackages، بالإضافة إلى مصفوفات الراستر الفضائية (Raster Tensors) متعددة الأطياف والاستعلامات المكانية الحسابية.",
                        "يتم استيعاب كافة الطبقات المكانية في بيئات معالجة معزولة ومؤقتة في الذاكرة (Memory Enclaves) حصرياً لتنفيذ التحليلات المطلوبة."
                    ]
                },
                {
                    heading: "٣. ضمانات انعدام التسريب وعدم التدريب (Zero-Egress)",
                    content: [
                        "حظر التدريب على بيانات العملاء: تتعهد VEXL تعهداً تعاقدياً صارماً وغير قابل للنقض بعدم استخدام حدودك المكانية، أو مضلعاتك الجغرافية، أو صور الأقمار الصناعية الخاصة بك، أو سجلات محادثاتك لتدريب أو ضبط أو معايرة أي نماذج ذكاء اصطناعي عامة أو تابعة لأطراف ثالثة.",
                        "المعالجة الآنية المعزولة: تتم معالجة ملفات الشيب فايل والراستر في الذاكرة اللحظية ولا يتم حفظها على وسائط تخزين دائمة عامة دون أمر صريح بالأرشفة من المستخدم.",
                        "الجاهزية للنشر المعزول (Air-Gapped): لعملاء القطاعات الحساسة والمؤسسات الدفاعية، توفر VEXL إمكانية النشر الداخلي المغلق شبكياً دون أي تدفق للبيانات أو القياسات عن بُعد خارج النطاق السيادي للمؤسسة."
                    ]
                },
                {
                    heading: "٤. أمان البنية التحتية والتشفير المؤسسي",
                    content: [
                        "يتم تشفير كافة البيانات أثناء النقل باستخدام بروتوكول TLS 1.3 مع سرية أمامية كاملة، وتشفير البيانات في حالة السكون بمعيار AES-256 المتطور.",
                        "تخضع بنيتنا التحتية لمزودي خدمات سحابية معتمدين وفق معايير SOC 2 Type II وISO 27001، مع تطبيق صارم لقواعد الوصول المستند إلى الأدوار (RBAC) بمبدأ الحد الأدنى من الصلاحيات."
                    ]
                },
                {
                    heading: "٥. استبقاء البيانات والحذف والاسترداد",
                    content: [
                        "يحتفظ المستخدم بالسيادة المطلقة والكاملة على مساحات عمله المكانية، ومخرجات نصوص ArcPy، وسجلات التحليل.",
                        "يحق لك في أي وقت تصدير بياناتك التحليلية بالكامل أو طلب الحذف النهائي والتطهير الرقمي الشامل لكافة السجلات والمخازن المؤقتة من خلال إعدادات الحساب أو عبر التواصل معنا."
                    ]
                },
                {
                    heading: "٦. مسؤول حماية البيانات وقنوات التواصل",
                    content: [
                        "لأي استفسارات قانونية، أو توقيع اتفاقيات معالجة البيانات (DPA)، أو طلبات التدقيق والامتثال، يرجى التواصل مع مكتب حماية البيانات عبر البريد الرسمي: vexl.gis@gmail.com"
                    ]
                }
            ]
        },
        terms: {
            title: "شروط الخدمة واتفاقية الحوسبة المكانية",
            lastUpdated: "تاريخ السريان: إطلاق 2026 // الإصدار 2.4",
            sections: [
                {
                    heading: "١. الموافقة ونطاق عمل المنظومة",
                    content: [
                        "يمثل استخدامك لمنظومة VEXL، بما في ذلك نظام تشغيل VEXL Nova، وGeoAgent، وCropLens، وDroughtWatch، ومحركات تنفيذ نصوص ArcPy، وأدوات سطح المكتب، وواجهات برمجة التطبيقات، موافقةً قانونية ملزمة على شروط الخدمة هذه.",
                        "تشكل هذه الشروط عقداً رسمياً بينك (أو المؤسسة التي تمثلها) وبين شركة VEXL."
                    ]
                },
                {
                    heading: "٢. الملكية الفكرية والإيداع البرمجي الرسمي",
                    content: [
                        "أصول VEXL السيادية: منظومة VEXL بالكامل، بما في ذلك خوارزميات التوفيق المكاني (Tensor-Topology)، ومحرك الذكاء الاصطناعي الجيومكاني، وتصميم واجهات المستخدم، وكافة الأكواد البرمجية الأصلية، مسجلة ومحمية بموجب المعاهدات الدولية للملكية الفكرية ومسجلة رسمياً لدى الجهاز المصري للملكية الفكرية (رقم الإيداع القانوني 5007 طبقاً للقانون رقم 82 لسنة 2002 والقانون رقم 163 لسنة 2023).",
                        "ملكية مخرجات المستخدم: تحتفظ بكامل الملكية الحصرية لكافة نصوص ArcPy البرمجية، والنماذج المكانية، والخرائط المتجهية، والتقارير التي يتم إنشاؤها عبر استخدامك للمنظومة."
                    ]
                },
                {
                    heading: "٣. حصص الحوسبة المكانية والاشتراكات المؤسسية",
                    content: [
                        "يتم تقديم الوصول إلى الخدمة عبر باقات محددة (Spatial Starter، Research Scholar، Spatial Engineer، Sovereign Enterprise) تخضع لوحدات الحوسبة المكانية ومعدلات استدعاء الـ API.",
                        "يتم فوترة الاشتراكات وفق الجداول المعلنة، وتدعم المنظومة أوامر الشراء الحكومية والمؤسسية، واتفاقيات مستوى الخدمة (SLA)، وبوابات الدفع الإلكتروني المعتمدة.",
                        "يمكن للمستخدم إلغاء الاشتراك في أي وقت، مع استمرار الوصول حتى نهاية دورة الفوترة المدفوعة الحالية."
                    ]
                },
                {
                    heading: "٤. الدقة الحتمية والمسؤولية الهندسية",
                    content: [
                        "تلتزم VEXL بتطبيق الحسابات الجيوديسية الإهليلجية الدقيقة (Karney Geodesics)، والتحويلات الصارمة لنظم الإسناد (EPSG)، والفيزياء الإشعاعية للأطياف الفضائية (NDVI، NDWI).",
                        "ومع ذلك، تُقدم النتائج المكانية كأدوات دعم قرار ونمذجة تحليلية تقنية، ويبقى على عاتق المهندسين والمختصين المعتمدين مسؤولية المراجعة الحقلية والتحقق النهائي قبل اتخاذ القرارات الهندسية أو الإنشائية ذات الصلة بسلامة الأرواح والممتلكات."
                    ]
                },
                {
                    heading: "٥. الاستخدام المحظور وحماية المنظومة",
                    content: [
                        "يلتزم المستخدم بعدم: (أ) محاولة تفكيك أو هندسة عكسية أو استخراج أوزان نماذج VEXL؛ (ب) شن هجمات حجب الخدمة (DDoS) أو استخدام أدوات الكشط الآلي غير المصرح بها؛ (ج) استخدام المنظومة في أي أنشطة تسليح أو رقابة غير قانونية تخالف القوانين السارية.",
                        "أي انتهاك لسلامة المنظومة يمنح VEXL الحق الفوري في إيقاف الحساب وسحب مفاتيح الربط البرمجي دون إنذار مسبق."
                    ]
                },
                {
                    heading: "٦. القانون الحاكم والاختصاص القضائي",
                    content: [
                        "تخضع هذه الشروط وكافة النزاعات الناشئة عنها وتُفسر وفقاً لأحكام قوانين جمهورية مصر العربية.",
                        "يخضع الطرفان للاختصاص القضائي الحصري للمحاكم الاقتصادية والتجارية المختصة في مدينة القاهرة، مصر."
                    ]
                },
                {
                    heading: "٧. التعديلات وقنوات التواصل القانوني",
                    content: [
                        "تحتفظ VEXL بحق تحديث هذه الشروط لمواكبة التطورات التقنية أو التنظيمية، مع إخطار الحسابات المؤسسية قبل 30 يوماً من سريان أي تعديل جوهري.",
                        "للتواصل بشأن الاتفاقيات المؤسسية أو الإخطارات القانونية: vexl.gis@gmail.com"
                    ]
                }
            ]
        }
    },
    footer: {
      phrase: "التنبؤ في كل بكسل.",
      copyright: "VEXL © 2026.",
      links: {
        download: "تحميل التطبيق",
        pricing: "الأسعار",
        help: "مركز المساعدة"
      }
    }
  }
};
